import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaFilePdf } from 'react-icons/fa';
import { useFormatters } from '../context/LocalizationContext';
import { useTranslation } from '../localization/useAppTranslation';
import { ThemedSpinner } from './ThemedSpinner';
import { chatService } from '../services/chatService';
import { logger } from '../utils/logger';
import { createAdminDisplayFormatters } from '../utils/adminFormatters';
import type { MapData } from '../utils/mapToolRegistry';
import '../styles/PdfExportButton.css';

const moduleLogger = logger.getModule('PdfExportButton');
const PDF_EXPORT_EVENT_URL = 'speedgauge://pdf-export';

const LazyPdfMapSnapshot = React.lazy(() =>
  import('./PdfMapSnapshot').then((mod) => ({ default: mod.PdfMapSnapshot }))
);

export interface AssistantMessagePdfExportData {
  content: string;
  citations?: string[];
  messageId?: number | string;
  sessionId: string;
  timestamp: string;
  generatedAt?: string;
  userName?: string;
  customerId?: string | number;
  customerName?: string;
  vehicleGroupId?: string | number | null;
  vehicleGroupName?: string | null;
  mapData?: MapData | null;
  mapTitle?: string;
}

interface PdfExportButtonProps {
  getExportData: () => AssistantMessagePdfExportData;
  disabled?: boolean;
  className?: string;
  showLabel?: boolean;
}

const buildFilename = (messageId?: number | string): string => {
  const datePart = new Date().toISOString().slice(0, 10);
  const messagePart = messageId ? String(messageId).replace(/[^a-zA-Z0-9_-]/g, '-') : 'untracked';
  return `coach-ai-message-${messagePart}-${datePart}.pdf`;
};

interface AuditLabels {
  customer: string;
  customerId: string;
  vehicleGroup: string;
  vehicleGroupId: string;
  user: string;
  sessionId: string;
  messageId: string;
  messageTimestamp: string;
}

const auditRowsFor = (
  data: AssistantMessagePdfExportData,
  labels: AuditLabels,
  formatDateTime: (value?: string | null) => string,
): Array<[string, string]> => {
  const rows: Array<[string, string | number | null | undefined]> = [
    [labels.customer, data.customerName],
    [labels.customerId, data.customerId],
    [labels.vehicleGroup, data.vehicleGroupName],
    [labels.vehicleGroupId, data.vehicleGroupId],
    [labels.user, data.userName],
    [labels.sessionId, data.sessionId],
    [labels.messageId, data.messageId],
    [labels.messageTimestamp, formatDateTime(data.timestamp)],
  ];

  return rows
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
    .map(([label, value]) => [label, String(value)]);
};

export const PdfExportButton: React.FC<PdfExportButtonProps> = ({
  getExportData,
  disabled = false,
  className = '',
  showLabel = true,
}) => {
  const { t } = useTranslation('reports');
  const localeFormatters = useFormatters();
  const adminFormatters = useMemo(
    () => createAdminDisplayFormatters(localeFormatters),
    [localeFormatters],
  );
  const [isExporting, setIsExporting] = useState(false);
  const [activeExportData, setActiveExportData] = useState<AssistantMessagePdfExportData | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const mapCaptureRef = useRef<HTMLDivElement | null>(null);

  const auditLabels = useMemo<AuditLabels>(() => ({
    customer: t('audit.customer'),
    customerId: t('audit.customerId'),
    vehicleGroup: t('audit.vehicleGroup'),
    vehicleGroupId: t('audit.vehicleGroupId'),
    user: t('audit.user'),
    sessionId: t('audit.sessionId'),
    messageId: t('audit.messageId'),
    messageTimestamp: t('audit.messageTimestamp'),
  }), [t]);

  const buttonClass = [
    'pdf-export-button',
    isExporting && 'pdf-export-button--exporting',
    !showLabel && 'pdf-export-button--icon-only',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const startExport = useCallback(() => {
    if (disabled || isExporting) {
      return;
    }

    const exportData = getExportData();
    const messageId = Number(exportData.messageId);

    if (Number.isSafeInteger(messageId) && messageId > 0) {
      chatService.trackLinkClick(exportData.sessionId, {
        url: PDF_EXPORT_EVENT_URL,
        text: 'Export PDF',
        is_external: false,
        message_id: messageId,
      });
    } else {
      moduleLogger.debug('Skipping PDF export tracking without a valid message ID', {
        messageId: exportData.messageId,
      });
    }

    setHasError(false);
    setIsExporting(true);
    setIsMapReady(false);
    setActiveExportData({
      ...exportData,
      generatedAt: new Date().toISOString(),
    });
  }, [disabled, getExportData, isExporting]);

  useEffect(() => {
    if (!activeExportData) {
      return;
    }

    if (activeExportData.mapData && !isMapReady) {
      return;
    }

    let isCancelled = false;

    const runExport = async () => {
      try {
        await new Promise((resolve) => window.requestAnimationFrame(resolve));

        if (isCancelled) {
          return;
        }

        const filename = buildFilename(activeExportData.messageId);

        // Build the PDF from native jsPDF text calls (not a rasterized DOM
        // screenshot) so the exported document's text stays selectable,
        // searchable, and copyable. The Leaflet map is the one exception --
        // it still gets rasterized via html2canvas below, since Leaflet's
        // rendering can't be reproduced with plain PDF drawing primitives.
        // These are all dynamically imported so jsPDF, Remark, and the PDF
        // layout code stay out of the initial bundle until an export begins.
        const [
          { jsPDF },
          { renderMarkdownContent, renderSectionHeading, renderNoteText },
          { renderDocumentHeader, renderAuditRows, renderFooterOnAllPages },
          { createPdfLayout, ensureSpace, advance },
        ] =
          await Promise.all([
            import('jspdf'),
            import('../utils/pdfMarkdownRenderer'),
            import('../utils/pdfExportSections'),
            import('../utils/pdfTextDocument'),
          ]);

        const pdf = new jsPDF({ unit: 'in', format: 'letter', orientation: 'portrait' });
        const layout = createPdfLayout(pdf, {
          marginLeft: 0.45,
          marginRight: 0.45,
          marginTop: 0.45,
          marginBottom: 0.55,
        });

        renderDocumentHeader(layout, t('generatedAt', {
          timestamp: adminFormatters.formatLocalDateTime(activeExportData.generatedAt),
        }));
        renderMarkdownContent(layout, activeExportData.content);

        if (activeExportData.mapData && mapCaptureRef.current) {
          const html2canvasModule = await import('html2canvas');
          const html2canvas = html2canvasModule.default;
          const canvas = await html2canvas(mapCaptureRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
          });
          const imageData = canvas.toDataURL('image/jpeg', 0.95);
          const imageWidthIn = layout.contentWidth;
          const imageHeightIn = (imageWidthIn * canvas.height) / canvas.width;

          renderSectionHeading(layout, activeExportData.mapTitle || t('map.title'));
          ensureSpace(layout, imageHeightIn);
          pdf.addImage(imageData, 'JPEG', layout.marginLeft, layout.y, imageWidthIn, imageHeightIn);
          advance(layout, imageHeightIn + 0.06);
          renderNoteText(layout, t('staticMapCaption'));
        }

        if (activeExportData.citations && activeExportData.citations.length > 0) {
          renderSectionHeading(layout, t('dataSources'));
          activeExportData.citations.forEach((citation) => {
            renderMarkdownContent(layout, citation);
          });
        }

        renderAuditRows(layout, auditRowsFor(
          activeExportData,
          auditLabels,
          adminFormatters.formatLocalDateTime,
        ));
        renderFooterOnAllPages(pdf, {
          generatedBy: t('generatedBy'),
          confidential: t('pdf.confidential'),
          verificationDisclaimer: t('pdf.verificationDisclaimer'),
          page: (pageNumber, totalPages) => t('pdf.page', {
            page: adminFormatters.formatNumber(pageNumber),
            total: adminFormatters.formatNumber(totalPages),
          }),
        });

        pdf.save(filename);
      } catch (exportError) {
        moduleLogger.error('Failed to export Coach AI answer PDF', { error: exportError });
        setHasError(true);
      } finally {
        if (!isCancelled) {
          setIsExporting(false);
          setActiveExportData(null);
        }
      }
    };

    runExport();

    return () => {
      isCancelled = true;
    };
  }, [activeExportData, adminFormatters, auditLabels, isMapReady, t]);

  return (
    <>
      <button
        type="button"
        className={buttonClass}
        onClick={startExport}
        disabled={disabled || isExporting}
        title={t('exportDescription')}
        aria-label={t('exportDescription')}
      >
        {isExporting ? (
          <>
            <ThemedSpinner size="sm" className="pdf-export-button__spinner" ariaLabel={t('exportingPdf')} />
            {showLabel && t('exportingPdf')}
          </>
        ) : (
          <>
            <FaFilePdf aria-hidden="true" />
            {showLabel && t('exportPdf')}
          </>
        )}
      </button>
      {hasError && <span className="pdf-export-error" role="status">{t('exportFailed')}</span>}
      {activeExportData?.mapData && (
        <div className="pdf-export-stage" aria-hidden="true">
          <React.Suspense fallback={<div className="pdf-export-map-loading">{t('loadingMap')}</div>}>
            <LazyPdfMapSnapshot
              mapData={activeExportData.mapData}
              onReady={() => setIsMapReady(true)}
              mapElementRef={mapCaptureRef}
            />
          </React.Suspense>
        </div>
      )}
    </>
  );
};

export default PdfExportButton;